/**
 * GuestFeesPanel — iter197. Per-session guest fee admin panel.
 *
 * Shows each external guest with a status pill (uncharged / pending /
 * collected / waived / reversed) and one-tap actions:
 *   • Collect cash — opens a tiny dialog with the default fee pre-filled,
 *     admin tweaks if needed → credits the group fund.
 *   • Waive — comp the guest with an optional reason.
 *   • Reverse — refund a previously collected fee (audit row stays).
 *
 * Mounted in `SessionDetailPage` for completed sessions when the session
 * has external_guests.
 */
import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Coins, XCircle, RefreshCw, UserCheck, UserMinus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const STATUS_PILLS = {
  uncharged: { label: 'Not charged', cls: 'bg-muted/50 text-muted-foreground border-border' },
  pending:   { label: 'Pending',     cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  collected: { label: 'Collected',   cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  waived:    { label: 'Waived',      cls: 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30' },
  reversed:  { label: 'Reversed',    cls: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30' },
};

export default function GuestFeesPanel({ sessionId, isAdmin, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);  // current guest_id being acted on
  const [collectFor, setCollectFor] = useState(null);
  const [waiveFor, setWaiveFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sessions/${sessionId}/guest-fees`);
      setData(r.data);
    } catch (e) {
      // 403 expected for non-members — silently hide.
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  const onWaive = useCallback(async (guestId, reason) => {
    setActing(guestId);
    try {
      await api.post(`/sessions/${sessionId}/guest-fee/${guestId}/waive`, { reason: reason || undefined });
      toast.success('Guest fee waived');
      setWaiveFor(null);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Waive failed');
    } finally {
      setActing(null);
    }
  }, [sessionId, load, onChange]);

  const onReverse = useCallback(async (guestId) => {
    if (!window.confirm('Reverse this collected guest fee? The group fund will be debited.')) return;
    setActing(guestId);
    try {
      await api.post(`/sessions/${sessionId}/guest-fee/${guestId}/reverse`);
      toast.success('Reversed');
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reverse failed');
    } finally {
      setActing(null);
    }
  }, [sessionId, load, onChange]);

  if (loading) {
    return (
      <div className="rounded-2xl border-2 border-border bg-card p-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="guest-fees-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading guest fees…
      </div>
    );
  }
  if (!data || !data.guests || data.guests.length === 0) return null;

  const { summary, default_fee } = data;

  return (
    <div className="rounded-2xl border-2 border-border bg-card p-4 space-y-3" data-testid="guest-fees-panel">
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Coins className="w-4 h-4 text-primary" />
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.16em]">Guest fees</h3>
        </div>
        <span className="text-xs text-muted-foreground tabular-nums" data-testid="guest-fees-summary">
          ₹{Math.round(summary.total_collected).toLocaleString()} collected · ₹{Math.round(summary.total_pending).toLocaleString()} pending
        </span>
      </header>

      <div className="divide-y divide-border">
        {data.guests.map((g) => {
          const st = STATUS_PILLS[g.status] || STATUS_PILLS.uncharged;
          const isActing = acting === g.guest_id;
          return (
            <div key={g.guest_id} className="py-2.5 flex items-start gap-3 text-sm" data-testid={`guest-fee-row-${g.guest_id}`}>
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-violet-500/10 text-violet-600 dark:text-violet-300 text-xs font-bold">
                {(g.guest_name || 'G').slice(0, 1).toUpperCase()}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate flex items-center gap-2">
                  {g.guest_name}
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs uppercase tracking-wider font-bold border ${st.cls}`}>
                    {st.label}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {g.status === 'collected' && (
                    <>Collected ₹{Math.round(g.collected_cash || g.amount || 0)} by {g.collected_by_name || 'admin'}</>
                  )}
                  {g.status === 'pending' && <>₹{Math.round(g.amount || default_fee)} due</>}
                  {g.status === 'waived' && (
                    <>Waived{g.waived_reason ? ` · ${g.waived_reason}` : ''}</>
                  )}
                  {g.status === 'reversed' && <>Previously collected; refunded</>}
                  {g.status === 'uncharged' && <>Default fee ₹{Math.round(default_fee || 0)}</>}
                  {g.added_by_name ? ` · added by ${g.added_by_name}` : ''}
                </p>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1 shrink-0">
                  {(g.status === 'uncharged' || g.status === 'pending') && (
                    <Button
                      size="sm"
                      variant="default"
                      className="h-7 px-2 text-xs gap-1"
                      onClick={() => setCollectFor(g)}
                      disabled={isActing}
                      data-testid={`guest-fee-collect-${g.guest_id}`}
                    >
                      <UserCheck className="w-3 h-3" />
                      Collect
                    </Button>
                  )}
                  {g.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs gap-1"
                      onClick={() => setWaiveFor(g)}
                      disabled={isActing}
                      data-testid={`guest-fee-waive-${g.guest_id}`}
                    >
                      <UserMinus className="w-3 h-3" />
                      Waive
                    </Button>
                  )}
                  {g.status === 'collected' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs gap-1 text-red-600 hover:text-red-700"
                      onClick={() => onReverse(g.guest_id)}
                      disabled={isActing}
                      data-testid={`guest-fee-reverse-${g.guest_id}`}
                    >
                      {isActing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      Reverse
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Collect dialog */}
      <CollectDialog
        guest={collectFor}
        defaultFee={default_fee}
        sessionId={sessionId}
        onClose={() => setCollectFor(null)}
        onSuccess={() => { load(); onChange?.(); }}
      />

      {/* Waive dialog */}
      <WaiveDialog
        guest={waiveFor}
        onClose={() => setWaiveFor(null)}
        onConfirm={(reason) => onWaive(waiveFor.guest_id, reason)}
      />
    </div>
  );
}

function CollectDialog({ guest, defaultFee, sessionId, onClose, onSuccess }) {
  const [cash, setCash] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (guest) setCash(String(defaultFee || ''));
  }, [guest, defaultFee]);
  const open = Boolean(guest);
  const onConfirm = useCallback(async () => {
    const amt = parseFloat(cash);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/sessions/${sessionId}/guest-fee/${guest.guest_id}/collect`, { cash_amount: amt });
      toast.success(`Collected ₹${Math.round(amt)} from ${guest.guest_name}`);
      onClose();
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Collect failed');
    } finally {
      setSubmitting(false);
    }
  }, [cash, sessionId, guest, onClose, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-sm" data-testid="guest-fee-collect-dialog">
        <DialogHeader>
          <DialogTitle>Collect from {guest?.guest_name}</DialogTitle>
          <DialogDescription>
            Record cash you received from the guest. The amount is credited to the group fund.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="guest-fee-cash" className="text-xs font-semibold uppercase tracking-wider">Cash amount (₹)</Label>
            <Input
              id="guest-fee-cash"
              type="number"
              inputMode="decimal"
              min="0"
              value={cash}
              onChange={(e) => setCash(e.target.value)}
              data-testid="guest-fee-cash-input"
              className="mt-1 text-base"
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting} data-testid="guest-fee-collect-cancel">Cancel</Button>
          <Button onClick={onConfirm} disabled={submitting} data-testid="guest-fee-collect-confirm" className="gap-1.5">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {submitting ? 'Collecting…' : 'Mark collected'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WaiveDialog({ guest, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (guest) setReason(''); }, [guest]);
  const open = Boolean(guest);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="guest-fee-waive-dialog">
        <DialogHeader>
          <DialogTitle>Waive {guest?.guest_name}&apos;s fee</DialogTitle>
          <DialogDescription>
            Comp the guest. The audit row is preserved with your note for future reference.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="guest-fee-waive-reason" className="text-xs font-semibold uppercase tracking-wider">Reason (optional)</Label>
            <Textarea
              id="guest-fee-waive-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="guest-fee-waive-reason"
              className="mt-1 text-sm"
              placeholder="e.g. First-time visit"
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="guest-fee-waive-cancel">Cancel</Button>
          <Button onClick={() => onConfirm(reason)} variant="destructive" data-testid="guest-fee-waive-confirm" className="gap-1.5">
            <XCircle className="w-3.5 h-3.5" /> Waive
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
